import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { KeyRound, ArrowLeft, CheckCircle } from 'lucide-react';
import { API_BASE_URL } from '../services/api';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const role = searchParams.get('role') || 'student';
  const isTeacher = role === 'teacher';

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetToken, setResetToken] = useState('');

  const endpoint = isTeacher
    ? `${API_BASE_URL}/tutors/forgot-password`
    : `${API_BASE_URL}/auth/forgot-password`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Something went wrong.');
      setResetToken(data.resetToken);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoReset = () => {
    navigate(`/reset-password/${resetToken}?role=${role}`);
  };

  const accentColor = isTeacher ? 'text-purple-400' : 'text-accentBlue';
  const accentBg = isTeacher ? 'bg-purple-500/10 border-purple-500/20' : 'bg-accentBlue/10 border-accentBlue/20';
  const accentBtn = isTeacher
    ? 'bg-purple-600 hover:bg-purple-500 text-white'
    : 'bg-accentBlue text-black hover:scale-[1.02]';

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-200px)] px-6 py-12">
      <div className="w-full max-w-md bg-white/5 border border-white/10 p-8 rounded-[40px] backdrop-blur-xl">
        <div className="text-center mb-8">
          <div className={`inline-flex items-center gap-2 px-4 py-1.5 ${accentBg} border rounded-full ${accentColor} text-xs font-bold uppercase tracking-wider mb-4`}>
            {isTeacher ? 'Teacher' : 'Student'} · Forgot Password
          </div>
          <h2 className="text-3xl font-bold text-white flex items-center justify-center gap-2">
            <KeyRound size={28} className={accentColor} /> Reset Password
          </h2>
          <p className="text-gray-400 text-sm mt-2">Enter your email to receive a reset link</p>
        </div>

        {!resetToken ? (
          <>
            {error && (
              <div className="bg-red-500/10 border border-red-500 text-red-400 p-3 rounded-xl mb-6 text-sm text-center">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-darkBg border border-white/10 p-3.5 rounded-xl focus:border-accentBlue focus:ring-2 focus:ring-accentBlue/20 outline-none text-sm transition-all"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-3.5 rounded-2xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${accentBtn}`}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          </>
        ) : (
          <div className="text-center space-y-6">
            <div className="w-16 h-16 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle size={32} />
            </div>
            <div>
              <p className="text-white font-semibold text-lg mb-1">Reset link ready!</p>
              <p className="text-gray-400 text-sm">Click the button below to set your new password.</p>
            </div>
            <button
              onClick={handleGoReset}
              className={`w-full py-3.5 rounded-2xl font-bold transition-all ${accentBtn}`}
            >
              Go to Reset Password
            </button>
          </div>
        )}

        <div className="mt-6 text-center">
          <Link
            to={isTeacher ? '/teacher-login' : '/login'}
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition-colors"
          >
            <ArrowLeft size={13} /> Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
